// Shape a DRF error response into a single toast-able message.
//
// This is the last stop before ~50 toasts across the app, so it decides whether
// a Kazakh user reads Kazakh. Two rules matter:
//
//  1. NEVER show a raw field identifier. The old version returned
//     `${field}: ${msg}`, which put snake_case API names ("domain_subject",
//     "date_from") in front of end users in every language. Field-level detail
//     belongs inline on the form — which `Field` already renders via `error` —
//     not in a toast.
//
//  2. Server prose is passed through, because the server is responsible for
//     translating it: LocaleMiddleware reads the Accept-Language that
//     services/api.js now sends, so once the backend gettext catalogs are
//     compiled these messages arrive already localized.
//
// The durable fix is for the API to return a stable machine CODE and let the
// client own the wording — components/timetable/runErrors.js already does this
// with its CODE_KEYS map, and it is the pattern to copy. Until the backend
// exposes codes everywhere, passing prose through is the honest option: the
// alternative, discarding it for a generic fallback, would throw away real
// information like "a subject with this code already exists".

// Bodies longer than this are not messages — they are HTML error pages from
// nginx or a traceback from an unhandled 500. Showing either to a user is
// worse than showing the caller's own wording.
const MAX_SERVER_MESSAGE = 300;

const usable = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_SERVER_MESSAGE) return false;
  return !trimmed.startsWith("<"); // HTML error page
};

const firstMessage = (value) => {
  if (Array.isArray(value)) return value.map(firstMessage).filter(Boolean).join(", ");
  if (value && typeof value === "object") {
    // Nested serializer errors: {"settings": {"lesson_minutes": ["..."]}}
    return Object.values(value).map(firstMessage).filter(Boolean).join(", ");
  }
  return typeof value === "string" ? value : "";
};

export function formatApiError(err, fallback) {
  const data = err?.response?.data;

  if (typeof data === "string") return usable(data) ? data.trim() : fallback;

  if (data && typeof data === "object") {
    // DRF's whole-request shapes, in the order it uses them. `detail` covers
    // APIException, permissions and simplejwt; `non_field_errors` covers
    // serializer-level validate().
    for (const key of ["detail", "non_field_errors"]) {
      const message = firstMessage(data[key]);
      if (usable(message)) return message.trim();
    }

    // Otherwise the first field error, message only — never the field name.
    for (const value of Object.values(data)) {
      const message = firstMessage(value);
      if (usable(message)) return message.trim();
    }
  }

  return fallback;
}
